import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Activity,
  BriefcaseBusiness,
  CalendarDays,
  ChevronDown,
  Eye,
  EyeOff,
  LogOut,
  MessageCircleMore,
  MessagesSquare,
  MessageSquareText,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  ListChecks,
  Settings2,
  UsersRound
} from "lucide-react";
import type { Session } from "../api.js";
import { Avatar } from "./ui.js";
import { TrialEndedNotice } from "./business-trial-status.js";
import { WorkspaceChromeContext, WorkspaceToolbar } from "./workspace-toolbar.js";
import { WhatsAppAttentionMark, WhatsAppConnectionNotice, whatsAppAttention } from "./whatsapp-connection-notice.js";

export type AppView = "overview" | "ops" | "work" | "calendar" | "clients" | "activity" | "conversations" | "whatsapp" | "settings";
export type OpsWorkspaceMode = "today" | "calendar" | "board" | "approvals";

interface NavigationItem {
  id: AppView;
  label: string;
  icon: typeof BriefcaseBusiness;
  mobileLabel?: string;
  managerOnly?: boolean;
}

interface OpsNavigationItem {
  id: OpsWorkspaceMode;
  label: string;
  railLabel?: string;
  icon: typeof BriefcaseBusiness;
}

const OPS_NAVIGATION: readonly OpsNavigationItem[] = [
  // WHY [BOL-OPS-CHAT-NAME-001]: this route is the persistent Ops conversation,
  // not a dated briefing. Keep its internal `today` id so history/state remain
  // stable, but name the same destination Ops Chat in desktop, mobile, and the
  // workspace header. Falsifier: any route-level label still says “Today,” or
  // this naming-only correction remounts/forks the conversation.
  { id: "today", label: "Ops Chat", icon: MessageSquareText },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "board", label: "Tasks", icon: BriefcaseBusiness },
  { id: "approvals", label: "Approvals", icon: ListChecks }
];

const MORE_NAVIGATION: readonly NavigationItem[] = [
  // Pairing and identity maintenance are occasional administration, not part
  // of the manager's daily approval loop. Keeping WhatsApp here preserves the
  // full QR/reconnect surface without making the operator think it is daily work.
  { id: "clients", label: "Clients & projects", mobileLabel: "Clients", icon: UsersRound },
  { id: "conversations", label: "Conversations", icon: MessagesSquare, managerOnly: true },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircleMore, managerOnly: true },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "settings", label: "Settings", icon: Settings2 }
];

const VIEW_LABELS: Record<AppView, string> = {
  overview: "Ops Agent",
  ops: "Ops Agent",
  work: "Tasks",
  calendar: "Calendar",
  clients: "Clients & projects",
  activity: "Activity",
  conversations: "Conversations",
  whatsapp: "WhatsApp",
  settings: "Settings"
};

function navigationFor(canManage: boolean) {
  return {
    primaryNavigation: OPS_NAVIGATION,
    moreNavigation: MORE_NAVIGATION.filter((item) => !item.managerOnly || canManage)
  };
}

export function shellNavigationLabels(canManage: boolean) {
  const navigation = navigationFor(canManage);
  return {
    primary: navigation.primaryNavigation.map((item) => item.railLabel ?? item.label),
    mobile: navigation.primaryNavigation.map((item) => item.label),
    more: navigation.moreNavigation.map((item) => item.label)
  };
}

export function AppShell({ session, view, setView, opsMode, setOpsMode, logout, connection, actionCount, privateMode = true, privateModeLocked = false, setPrivateMode = () => undefined, notice = null, trialEnded = false, whatsAppStatus = null, children }: {
  session: Session;
  view: AppView;
  setView: (view: AppView) => void;
  opsMode: OpsWorkspaceMode;
  setOpsMode: (mode: OpsWorkspaceMode) => void;
  logout: () => void;
  connection: "live" | "refreshing";
  actionCount: number;
  privateMode?: boolean;
  privateModeLocked?: boolean;
  setPrivateMode?: (enabled: boolean) => void;
  // WHY [BOL-SELF-SERVE-WEB-004]: an optional one-line desk notice (the
  // server's trial phase) rendered in the fixed-height desktop top bar, so it
  // never adds height to the tuned Chat workspace. The phone header is 0 px
  // (BOL-MOBILE-MORE-IN-NAV-001); the notice's own CSS hides it there.
  notice?: ReactNode;
  trialEnded?: boolean;
  whatsAppStatus?: string | null;
  children: ReactNode;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  // WHY [BOL-SIDEBAR-ICON-RAIL-001]: reclaim work space without changing the
  // route, children, keys or Voice owner. This local class-only switch must
  // preserve the same composer/history/media nodes and a reachable reopen.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: the desktop top bar is gone; each view's own toolbar renders Private
  // from this one owner (App.tsx state + Voice presentation lock), so behavior is unchanged wherever it sits.
  const chrome = useMemo(() => ({ privateMode, privateModeLocked, setPrivateMode }), [privateMode, privateModeLocked, setPrivateMode]);
  const canManage = session.member.role === "OWNER" || session.member.role === "MANAGER";
  // WHY [BOL-WA-NOTICE-821]: reuse App's current 15s status, never add a poll
  // or infer a disconnect from saved pairing. Attention updates do not touch
  // route, children, keys, Voice, focus or scroll. Only an explicit click
  // follows the existing WhatsApp route and its existing page-owned recovery.
  const [attentionPending, setAttentionPending] = useState(false);
  const attention = canManage ? whatsAppAttention(whatsAppStatus, attentionPending) : null;
  useEffect(() => {
    if (!canManage || whatsAppStatus === "CONNECTED" || whatsAppStatus === null) setAttentionPending(false);
    else if (whatsAppAttention(whatsAppStatus)) setAttentionPending(true);
  }, [canManage, whatsAppStatus]);
  const { primaryNavigation, moreNavigation } = navigationFor(canManage);
  const secondaryActive = moreNavigation.some((item) => item.id === view);

  const navigate = (next: AppView) => {
    setMoreOpen(false);
    if (next === "ops") {
      setOpsMode("today");
      return;
    }
    setView(next);
  };

  const openOpsMode = (next: OpsWorkspaceMode) => {
    setMoreOpen(false);
    setOpsMode(next);
  };

  useEffect(() => {
    if (!moreOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [moreOpen]);

  return (
    <div className={`app-shell${sidebarCollapsed ? " app-shell--sidebar-collapsed" : ""}`}>
      <aside className="sidebar" id="workspace-sidebar">
        <button className="sidebar-toggle" type="button" aria-controls="workspace-sidebar" aria-expanded={!sidebarCollapsed} aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation to icons"} title={sidebarCollapsed ? "Expand navigation" : "Collapse navigation to icons"} onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}>
          {sidebarCollapsed ? <PanelLeftOpen size={20} aria-hidden="true" /> : <PanelLeftClose size={20} aria-hidden="true" />}
          <span>{sidebarCollapsed ? "Expand" : "Collapse"}</span>
        </button>
        <div className="brand-lockup" title={session.workspace.name}>
          <span className="brand-mark" aria-hidden="true"><img src="/assets/ops-cube-logo-r72-128.png" width={31} height={31} alt="" /></span>
          <div><strong>{session.workspace.name}</strong></div>
        </div>
        {/* WHY [BOL-CUBE-BRAND-R73]: only the cube and actual workspace name identify this business.
            Removed the generic desk label and routine board-loading row; genuine WhatsApp
            connection attention, trial notices and all navigation remain independent below. */}
        <nav className="primary-nav" aria-label="Main navigation">
          {primaryNavigation.map(({ id, label, railLabel, icon: Icon }) => (
            <button key={id} className={view === "ops" && opsMode === id ? "is-active" : ""} type="button" onClick={() => openOpsMode(id)} aria-label={railLabel ?? label} aria-current={view === "ops" && opsMode === id ? "page" : undefined} title={railLabel ?? label}>
              <Icon size={19} strokeWidth={1.8} aria-hidden="true" /><span>{railLabel ?? label}</span>{id === "approvals" && actionCount > 0 ? <small className="nav-count">{actionCount > 99 ? "99+" : actionCount}</small> : null}
            </button>
          ))}
          <div className="desktop-more">
            <button className={`wa-attention-target${secondaryActive || moreOpen ? " is-active" : ""}`} type="button" onClick={() => setMoreOpen((open) => !open)} aria-label={attention ? `More — ${attention}` : "More"} title={attention ? `More — ${attention}` : "More"} aria-expanded={moreOpen} aria-controls="desktop-more-menu">
              <MoreHorizontal size={19} aria-hidden="true" /><span>More</span><ChevronDown className="nav-chevron" size={15} aria-hidden="true" />
              {attention ? <WhatsAppAttentionMark /> : null}
            </button>
            {moreOpen ? <MoreMenu id="desktop-more-menu" items={moreNavigation} view={view} navigate={navigate} whatsAppAttention={attention} /> : null}
          </div>
        </nav>
        {notice ? <div className="bol-sidebar-notice">{notice}</div> : null}
        <div className="sidebar__bottom">
          <div className="profile-row profile-row--static">
            <Avatar name={session.member.displayName} />
            <span><strong>{session.member.displayName}</strong><small>{session.member.role.toLowerCase()}</small></span>
          </div>
          <button className="logout-button" type="button" onClick={logout} aria-label="Sign out" title="Sign out"><LogOut size={17} aria-hidden="true" /><span>Sign out</span></button>
        </div>
      </aside>

      <div className="workspace-shell">
        <header className="topbar">
          <div className="topbar__context">
            <strong>{view === "ops" ? primaryNavigation.find((item) => item.id === opsMode)?.label ?? "Ops Agent" : VIEW_LABELS[view]}</strong>
            <span><i className={`live-dot ${connection === "refreshing" ? "is-refreshing" : ""}`} />{connection === "live" ? "Workspace live" : "Updating workspace"}</span>
          </div>
          <div className="topbar__actions">
            {/* WHY [BOL-APP-WORKSPACE-TOOLBAR-001]: Private moved into each view's toolbar (desktop) and stays in the
                phone rail; the notice moved to the sidebar. This bar is hidden on desktop and 0 px on phones. */}
            <div className="topbar-more">
              <button className="wa-attention-target" type="button" onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen} aria-controls="mobile-more-menu" aria-label={attention ? `Open workspace menu — ${attention}` : "Open workspace menu"}><MoreHorizontal size={20} aria-hidden="true" />{attention ? <WhatsAppAttentionMark /> : null}</button>
              {/* WHY [BOL-COMPACT-NAV-ESCAPE-001]: route changes replace the
                  current history entry, so browser Back cannot be the only way
                  out of a secondary phone view. Keep an explicit Ops Agent home
                  and Sign out in this compact menu without changing desktop
                  navigation. Falsifier: a 360px secondary view strands the operator. */}
              {moreOpen ? <CompactMoreMenu id="mobile-more-menu" canManage={canManage} view={view} navigate={navigate} logout={() => { setMoreOpen(false); logout(); }} whatsAppAttention={attention} /> : null}
            </div>
          </div>
        </header>
        <WorkspaceChromeContext.Provider value={chrome}>
          <main className="workspace">
            {/* WHY [BOL-TRIAL-NOTICE-COLLISION-876]: the fixed reconnect popup
                covered the expired-trial payment action on phones. Keep one
                notice instance and its dismissal state; only expired mobile
                CSS places it in flow above the payment notice. The workspace,
                children and Voice owner never move or acquire a new key. */}
            <WhatsAppConnectionNotice message={attention} status={whatsAppStatus} visible={view !== "whatsapp"} openWhatsApp={() => navigate("whatsapp")} />
            {/* The same canonical expired phase needs a phone action outside
                the zero-height header. Never remount children or add a second
                subscription read; nonexpired mobile layout stays unchanged. */}
            {trialEnded ? <TrialEndedNotice variant="panel" mobileOnly /> : null}
            {/* Secondary pages carry their own page heading, so their toolbar title is for screen readers only. */}
            {view !== "ops" ? <WorkspaceToolbar variant="utility" title={VIEW_LABELS[view]} titleHidden /> : null}
            {children}
          </main>
        </WorkspaceChromeContext.Provider>
      </div>

      <nav className="mobile-nav" aria-label="Ops Agent tools">
        {primaryNavigation.map(({ id, label, icon: Icon }) => (
          <button key={id} className={view === "ops" && opsMode === id ? "is-active" : ""} type="button" onClick={() => openOpsMode(id)} aria-current={view === "ops" && opsMode === id ? "page" : undefined}>
            <span className="mobile-nav__icon"><Icon size={20} aria-hidden="true" />{id === "approvals" && actionCount > 0 ? <small className="nav-count">{actionCount > 99 ? "99+" : actionCount}</small> : null}</span><span>{label}</span>
          </button>
        ))}
        <button className={"private-mode-toggle--mobile " + (privateMode ? "is-active" : "")} type="button" aria-label="Private mode" aria-pressed={privateMode} disabled={privateModeLocked} title={privateModeLocked ? "Private mode is fixed until the current Voice request finishes." : undefined} onClick={() => setPrivateMode(!privateMode)}>
          <span className="mobile-nav__icon">{privateMode ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}</span><span>Private</span>
        </button>
      </nav>
    </div>
  );
}

export function CompactMoreMenu({ id, canManage, view, navigate, logout, whatsAppAttention = null }: {
  id: string;
  canManage: boolean;
  view: AppView;
  navigate: (view: AppView) => void;
  logout: () => void;
  whatsAppAttention?: string | null;
}) {
  return <MoreMenu
      id={id}
      items={navigationFor(canManage).moreNavigation}
      view={view}
      navigate={navigate}
      includeHome
      logout={logout}
      whatsAppAttention={canManage ? whatsAppAttention : null}
    />;
}

function MoreMenu({ id, items, view, navigate, includeHome = false, logout, whatsAppAttention = null }: {
  id?: string;
  items: readonly NavigationItem[];
  view: AppView;
  navigate: (view: AppView) => void;
  includeHome?: boolean;
  logout?: () => void;
  whatsAppAttention?: string | null;
}) {
  return (
    <div id={id} className="more-menu">
      {includeHome ? <div className="more-menu__item more-menu__item--home">
        <button className={view === "ops" ? "is-active" : ""} type="button" onClick={() => navigate("ops")} aria-current={view === "ops" ? "page" : undefined}>
          <MessageSquareText size={19} aria-hidden="true" /><span>Ops Agent</span>
        </button>
      </div> : null}
      {items.map(({ id: itemId, label, icon: Icon }) => (
        <div className="more-menu__item" key={itemId}>
          <button className={`wa-attention-target${view === itemId ? " is-active" : ""}`} type="button" onClick={() => navigate(itemId)} aria-current={view === itemId ? "page" : undefined} aria-label={itemId === "whatsapp" && whatsAppAttention ? `WhatsApp — ${whatsAppAttention}` : undefined}>
            <Icon size={19} aria-hidden="true" /><span>{label}{itemId === "whatsapp" && whatsAppAttention ? <small className="wa-attention-caption">{whatsAppAttention}</small> : null}</span>
            {itemId === "whatsapp" && whatsAppAttention ? <WhatsAppAttentionMark /> : null}
          </button>
        </div>
      ))}
      {logout ? <div className="more-menu__item more-menu__item--signout">
        <button type="button" onClick={logout}><LogOut size={19} aria-hidden="true" /><span>Sign out</span></button>
      </div> : null}
    </div>
  );
}
